import h from 'hyperscript';

import { showToastMessage } from '@disy/ui/toast-message';
import { getSettingOrSettingDefault } from '@disy/workbook.lib/settings';

import 'cadenza/ui/attribute-tile/attribute-tile';
import type { AttributeTile } from 'cadenza/ui/attribute-tile/attribute-tile';
import { createBundleAttributeLimitConfigurations } from 'cadenza/workbook/advanced-data-settings-dialog/advanced-data-settings-dialog-utils';
// we use the css class .d-attribute-picker for css styling here
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { DataView } from 'cadenza/workbook/data-view/data-view';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import { TableDataViewSettingsKey } from 'cadenza/workbook/model/table-data-view-settings-key.gen';
import type { CompatibleBundleProvider } from 'cadenza/workbook/workbook-panel/settings/attributes/compatible-bundle-provider';
import type { DataViewBundleControllerOptions } from 'cadenza/workbook/workbook-panel/settings/attributes/data-view-bundle-controller';
import { DataViewBundleController } from 'cadenza/workbook/workbook-panel/settings/attributes/data-view-bundle-controller';

import i18n from './table-columns-drop-controller.properties';

export const MEASURES_SUMMARIZED_ATTRIBUTE_ID = 'MeasuresSummarizedId' as ActiveAttributeId;

const OPTIONS: DataViewBundleControllerOptions = {
  dropPanelPlaceholderMessage: i18n('settings.bundle.columns.placeholder'),
  viewCoverAddActionMessage: i18n('settings.bundle.columns.addAction'),
};

export class TableColumnsDropController extends DataViewBundleController {
  constructor(dataView: DataView, compatibleBundleProvider: CompatibleBundleProvider) {
    super(dataView, 'COLUMNS', { ...OPTIONS, compatibleBundleProvider });
  }

  /**
   * Checks if we have to show the virtual "measures summarized" column with the current state.
   * This is defined by "MeasuresSummarizedIndex" setting in dataView.
   *
   * @return True if the column should be shown, else false.
   */
  _isMeasuresSummarizedEnabled() {
    const settingValue = getSettingOrSettingDefault<number>(
      this.dataView,
      TableDataViewSettingsKey.toPersistenceKey('VIRTUAL_MEASURES_SUMMARIZED_COLUMN_INDEX'),
    );
    return settingValue > -1;
  }

  override async add(attributeId: AttributeId, index?: number) {
    let dataViewSettings; // Don't change settings as default;
    if (this._isMeasuresSummarizedEnabled()) {
      let measuresSummarizedIndex = this._getMeasureSummarizedIndex();
      if (index === undefined) {
        // do not adjust index, keep it undefined: undefined is used in the backend to mean "insert
        // after last element". no need to adjust measuresSummarizedIndex: we insert ourselves last.
      } else if (measuresSummarizedIndex < index) {
        index -= 1;
      } else {
        measuresSummarizedIndex += 1;
      }
      dataViewSettings = {
        [TableDataViewSettingsKey.toPersistenceKey('VIRTUAL_MEASURES_SUMMARIZED_COLUMN_INDEX')]:
          measuresSummarizedIndex,
      };
    }
    this.dataView.addAttribute(attributeId, this.bundleName, index, dataViewSettings);
    const isSwitchToPivot = this.dataView.getAttributeCountAndLimitsPerBundle()[this.bundleName]!.current === 0;
    if (isSwitchToPivot) {
      const pivotRowDimensionLimit = createBundleAttributeLimitConfigurations(this.dataView).filter(
        (limitConfig) =>
          limitConfig.key === TableDataViewSettingsKey.toPersistenceKey('PIVOT_TABLE_MAX_ROW_DIMENSIONS'),
      )[0].max;
      if (this.dataView.getAttributeCountAndLimitsPerBundle().ROWS!.current > pivotRowDimensionLimit) {
        showToastMessage(i18n('rowAttributesMoved.toastMessage'), { parentElement: this.#findParentViewForDataView() });
      }
    }
  }

  #findParentViewForDataView() {
    return document.querySelector<HTMLElement>(`.d-worksheet-view[data-id='${this.dataView.viewId}']`) ?? undefined;
  }

  override remove(attributeId: ActiveAttributeId) {
    let dataViewSettings; // Don't change settings as default;
    if (this._isMeasuresSummarizedEnabled()) {
      const indexOfAttribute = super.attributes.map((attribute) => attribute.id).indexOf(attributeId);
      let measuresSummarizedIndex = this._getMeasureSummarizedIndex();
      if (indexOfAttribute <= measuresSummarizedIndex) {
        measuresSummarizedIndex -= 1;
      }
      dataViewSettings = {
        [TableDataViewSettingsKey.toPersistenceKey('VIRTUAL_MEASURES_SUMMARIZED_COLUMN_INDEX')]:
          measuresSummarizedIndex,
      };
    }
    this.dataView.removeAttribute(attributeId, dataViewSettings);
  }

  override reorder(attributeIds: ActiveAttributeId[]) {
    let dataViewSettings; // Don't change settings as default;
    const index = attributeIds.indexOf(MEASURES_SUMMARIZED_ATTRIBUTE_ID);
    if (index >= 0) {
      dataViewSettings = {
        [TableDataViewSettingsKey.toPersistenceKey('VIRTUAL_MEASURES_SUMMARIZED_COLUMN_INDEX')]: index,
      };
      attributeIds.splice(index, 1);
    }
    this.dataView.reorderAttributes(this.bundleName, attributeIds, dataViewSettings);
  }

  override get attributes() {
    const attributes = [...super.attributes];
    if (this._isMeasuresSummarizedEnabled()) {
      attributes.splice(this._getMeasureSummarizedIndex(), 0, {
        id: MEASURES_SUMMARIZED_ATTRIBUTE_ID,
      } as unknown as WorkbookAttributeDto);
    }
    return attributes;
  }

  _getMeasureSummarizedIndex() {
    const index = getSettingOrSettingDefault<number>(
      this.dataView,
      TableDataViewSettingsKey.toPersistenceKey('VIRTUAL_MEASURES_SUMMARIZED_COLUMN_INDEX'),
    );
    return Math.min(index, super.attributes.length);
  }

  override createElement(attribute: WorkbookAttributeDto) {
    if (attribute.id === MEASURES_SUMMARIZED_ATTRIBUTE_ID) {
      return this.createMeasureSummarizeAttributeElement();
    } else {
      return super.createElement(attribute);
    }
  }

  createMeasureSummarizeAttributeElement() {
    const element = h<AttributeTile>('li.d-attribute-tile', {
      'data-id': MEASURES_SUMMARIZED_ATTRIBUTE_ID,
    });
    element.append(
      h('.attribute-content-container', [h('.attribute-name', i18n('settings.bundle.columns.measures-summary.name'))]),
    );
    return element;
  }
}
